/*----------------------------------
- REFUND POLICY PAGE CONTENT CATALOG (SSOT)
----------------------------------*/

import { PAYMENT_FAILURE_GRACE_LABEL } from '@uniquedomains/components/pricing/catalogs/paymentGrace';
import { REFUND_GUARANTEE_DAYS } from '@uniquedomains/components/pricing/catalogs/refundPromise';
import type { LegalPageContent } from '@/client/catalogs/landing/legal/types';

/**
 * The guarantee's days come from `REFUND_GUARANTEE_DAYS`; the failed-payment rule is
 * `PAYMENT_FAILURE_GRACE_HOURS` (`paymentGrace.ts`, D23 of the paid-funnel plan); credit top-ups left with
 * the paid-only access model (ADR-0083).
 *
 * Grounded in shipped behavior: refunds are handled manually through Stripe
 * (`charge.refunded` handling in `apps/product/server/services/Conversion/Plans`),
 * and duplicate subscriptions are
 * detected but never refunded automatically, per `docs/features/billing/README.md`.
 *
 * The withdrawal wording assumes an EU-registered trader selling to consumers.
 * Confirm it against the final entity before this page is indexed.
 */

export const REFUND_POLICY_PAGE_CONTENT: LegalPageContent = {
    meta: {
        pageTitle: 'Refund Policy | Unique Domains',
        metaDescription:
            'When you can get a refund from Unique Domains, how the EU right of withdrawal applies to immediate access, and how to ask.',
    },
    hero: {
        heading: 'Refund Policy',
        intro: 'What you can get back, when, and how to ask for it. If your case is not covered here, write to us and we will look at it.',
    },
    sections: [
        {
            id: 'scope',
            heading: '1. What this covers',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'This page covers payments made to Unique Domains: Full Access subscriptions, their extra seats, and payments made on plans we no longer sell.',
                },
                {
                    kind: 'paragraph',
                    text: 'It does not cover money you spend with a registrar or a marketplace. We do not sell domain names and we take no part in that transaction, so a refund there is a matter between you and the seller.',
                },
            ],
        },
        {
            id: 'guarantee',
            heading: `2. The ${REFUND_GUARANTEE_DAYS}-day guarantee`,
            blocks: [
                {
                    kind: 'paragraph',
                    text: `If the product is not for you, tell us within ${REFUND_GUARANTEE_DAYS} days of paying and we refund you in full. No conditions and no questions: it applies even if you already ran analyses, appraisals, or Radars.`,
                },
                {
                    kind: 'paragraph',
                    text: 'This is deliberately simpler than the rules below rather than a replacement for them. The withdrawal right in the next section runs longer, to 14 days, and the sections after it cover renewals, duplicates and monthly limits. Whichever gives you more, you get.',
                },
                {
                    kind: 'paragraph',
                    text: 'One refund per customer under this guarantee. It is there so you can try the product without weighing the risk, not as a way to use it for free on repeat.',
                },
            ],
        },
        {
            id: 'withdrawal',
            heading: '3. Your right of withdrawal',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'If you are a consumer in the European Union, you normally have 14 days to withdraw from a distance contract without giving a reason.',
                },
                {
                    kind: 'paragraph',
                    text: 'Access starts as soon as you pay. We do not ask you to give up that right in exchange, so it stands: tell us within 14 days and we will refund you.',
                },
                {
                    kind: 'paragraph',
                    text: 'If you already used the plan during those 14 days, we may keep the value of what you used and refund the rest. If you paid and did not use it, we refund the full amount.',
                },
            ],
        },
        {
            /*
             * This section sold Launch at $29 on a page nobody can buy it from:
             * checkout refuses the plan. It has to stay, because the people who
             * hold one can still ask for a refund, so it states the retirement
             * first and then what they are owed.
             */
            id: 'launch',
            heading: '4. Plans we no longer sell',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Launch, Pro, Agency and the earlier Full Access prices can no longer be bought. If you hold one of those subscriptions, it now carries Full Access, renews at the price you purchased it at, and everything on this page applies to it.',
                },
                {
                    kind: 'paragraph',
                    text: 'A Launch bought as a single payment was one month of access. It does not renew, so there is no future charge to stop. If you have not run a domain analysis or an appraisal, ask us within 14 days of paying and we will refund you in full.',
                },
            ],
        },
        {
            id: 'pro',
            heading: '5. Full Access subscriptions',
            blocks: [
                {
                    /*
                     * This opened on the 3-day trial and called cancelling before
                     * it ended the cleanest way to decide. There is no trial to
                     * cancel: the card is charged at checkout, so a reader who
                     * followed that sentence was charged $19 expecting nothing.
                     * The guarantee in section 2 is what took its place.
                     */
                    kind: 'paragraph',
                    text: `Full Access carries no trial. Your card is charged when you check out, so the ${REFUND_GUARANTEE_DAYS}-day guarantee in section 2 is the way to decide whether the plan is right for you: tell us within ${REFUND_GUARANTEE_DAYS} days and we refund you in full.`,
                },
                {
                    kind: 'list',
                    items: [
                        'Cancelling stops the next renewal. Your plan stays active until the end of the period you already paid for, and that period is not refunded by default.',
                        'If you were charged for a renewal you did not intend, and you have not used the plan during that new period, write to us within 14 days and we will refund it.',
                        'If you were charged twice for the same plan, we refund the duplicate in full. We do not detect and reverse these automatically, so please tell us.',
                    ],
                },
            ],
        },
        {
            /* "6. Credit top-ups" until ADR-0083: no credit is sold any more, so the section states
               what the monthly limits are worth instead. */
            id: 'limits',
            heading: '6. Monthly limits',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'The monthly limits of Full Access, such as the analyses included each month, are not refundable units: they come with the plan and have no cash value of their own.',
                },
                {
                    kind: 'paragraph',
                    text: 'If a run fails on our side, the part that failed does not count against your limits. If it was counted anyway, tell us and we will put it back.',
                },
            ],
        },
        {
            id: 'failed-payments',
            heading: '7. Failed payments',
            blocks: [
                {
                    kind: 'paragraph',
                    text: `A refused payment is not a charge, so there is nothing to refund. After a refused renewal, Full Access stays open for ${PAYMENT_FAILURE_GRACE_LABEL} so you can update your payment method; after that the plan ends until the payment goes through.`,
                },
            ],
        },
        {
            id: 'how-to-ask',
            heading: '8. How to ask',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Email the contact address at the top of this page with the address on your account and the date of the payment. You do not need to explain yourself.',
                },
                {
                    kind: 'paragraph',
                    text: 'We answer within one working day. Approved refunds go back to the payment method you used, through Stripe, and usually arrive within 10 working days depending on your bank.',
                },
            ],
        },
        {
            id: 'chargebacks',
            heading: '9. Before you open a chargeback',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Please write to us first. A chargeback takes weeks and locks the payment while it runs, and almost every case we see is faster to fix directly.',
                },
            ],
        },
    ],
};
